import React, { useState, useEffect, useMemo } from "react";
import { Search, Plus, Copy, Pencil, Power, Award, Users, BadgeCheck } from "lucide-react";
import { StatTile } from "../../components/ui/StatTile";
import toast from "react-hot-toast";
import { AppShell, headerPrimaryButtonClass } from "../../components/Layout/AppShell";
import { Modal } from "../../components/ui/Modal";
import { ConfirmModal } from "../../components/ui/ConfirmModal";
import { Badge } from "../../components/ui/Badge";
import { CheckboxField } from "../../components/ui/CheckboxField";
import { getAmbassadors, createAmbassador, updateAmbassador } from "../../business/AdminBusiness";
import { createUser, getAllUsers } from "../../business/UserBusiness";
import { ensureCountryCode } from "../../lib/phone";
import { normalizeReferralCode, isValidReferralCode } from "../../lib/referralCode";

const inputClass = "w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent";
const labelClass = "block text-sm font-medium text-gray-700 mb-1";
const primaryButtonClass = "inline-flex items-center justify-center rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-60 flex-1";
const secondaryButtonClass = "rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex-1";

const EMPTY_FORM = {
  mode: "existing", // "existing" = anexar usuária que já existe | "new" = criar conta nova
  selectedUser: null,
  username: "",
  email: "",
  phone: "",
  password: "",
  code: "",
  dispatchesDisabled: true, // embaixadora nasce sem disparos (call de 23/09) — dá pra desmarcar
};


export const Ambassadors = () => {
  const [ambassadors, setAmbassadors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [allUsers, setAllUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");

  const [editingCode, setEditingCode] = useState(null); // { _id, username, code }
  const [toggleTarget, setToggleTarget] = useState(null);

  const fetchAmbassadors = async () => {
    try {
      setIsLoading(true);
      const res = await getAmbassadors();
      setAmbassadors(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      toast.error(error?.response?.data?.msg || "Erro ao carregar embaixadoras");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAmbassadors();
  }, []);

  const openCreateModal = async () => {
    setForm(EMPTY_FORM);
    setUserSearch("");
    setShowCreateModal(true);
    try {
      const res = await getAllUsers();
      setAllUsers(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      toast.error("Não foi possível carregar a lista de usuárias");
    }
  };

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const candidateUsers = useMemo(() => {
    const query = userSearch.trim().toLowerCase();
    if (!query) return [];
    return allUsers
      .filter((user) => !user.isAmbassador && !user.ambassadorCode)
      .filter((user) => `${user.username || ""} ${user.email || ""}`.toLowerCase().includes(query))
      .slice(0, 6);
  }, [allUsers, userSearch]);

  const filteredAmbassadors = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return ambassadors;
    return ambassadors.filter((ambassador) =>
      `${ambassador.username || ""} ${ambassador.email || ""} ${ambassador.code || ""}`.toLowerCase().includes(query)
    );
  }, [ambassadors, searchTerm]);

  const totals = useMemo(() => ({
    active: ambassadors.filter((ambassador) => ambassador.isAmbassador).length,
    signups: ambassadors.reduce((sum, ambassador) => sum + ambassador.signups, 0),
    subscriptions: ambassadors.reduce((sum, ambassador) => sum + ambassador.activeSubscriptions, 0),
  }), [ambassadors]);

  const handleCreate = async () => {
    if (!isValidReferralCode(form.code)) {
      toast.error("Código inválido: use de 3 a 20 letras ou números.");
      return;
    }

    try {
      setIsSaving(true);
      let userId = form.selectedUser?._id;

      if (form.mode === "existing") {
        if (!userId) {
          toast.error("Selecione a usuária que vai virar embaixadora");
          return;
        }
      } else {
        if (!form.username || !form.email || !form.phone || !form.password) {
          toast.error("Preencha nome, e-mail, telefone e senha");
          return;
        }
        const res = await createUser({
          username: form.username,
          email: form.email,
          phone: ensureCountryCode(form.phone),
          password: form.password,
          role: "user",
        });
        userId = res?.data?.user?._id || res?.data?._id;
        if (!userId) {
          toast.error("A conta foi criada, mas não consegui identificá-la. Cadastre a embaixadora pela lista.");
          return;
        }
      }

      try {
        await createAmbassador({ userId, code: form.code, disableDispatches: form.dispatchesDisabled });
      } catch (error) {
        const reason = error?.response?.data?.msg || "erro ao cadastrar a embaixadora";
        toast.error(form.mode === "new" ? `Conta criada, mas ${reason}. Anexe pela opção "Usuária existente".` : reason, { duration: 8000 });
        fetchAmbassadors();
        return;
      }

      toast.success("Embaixadora cadastrada!");
      setShowCreateModal(false);
      fetchAmbassadors();
    } catch (error) {
      toast.error(error?.response?.data?.msg || "Erro ao cadastrar embaixadora");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveCode = async () => {
    if (!isValidReferralCode(editingCode.code)) {
      toast.error("Código inválido: use de 3 a 20 letras ou números.");
      return;
    }
    try {
      setIsSaving(true);
      await updateAmbassador(editingCode._id, { code: editingCode.code });
      toast.success("Código atualizado");
      setEditingCode(null);
      fetchAmbassadors();
    } catch (error) {
      toast.error(error?.response?.data?.msg || "Erro ao atualizar o código");
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async () => {
    try {
      await updateAmbassador(toggleTarget._id, { isAmbassador: !toggleTarget.isAmbassador });
      toast.success(toggleTarget.isAmbassador ? "Embaixadora desativada" : "Embaixadora reativada");
      setToggleTarget(null);
      fetchAmbassadors();
    } catch (error) {
      toast.error(error?.response?.data?.msg || "Erro ao atualizar a embaixadora");
      setToggleTarget(null);
    }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`Código ${code} copiado`);
    } catch (error) {
      toast.error("Não foi possível copiar");
    }
  };

  return (
    <>
      <AppShell
        icon={Award}
        eyebrow="Crescimento"
        title="Embaixadoras"
        meta="Ranking de indicações e códigos do programa"
      >
          <div className="space-y-6">

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <StatTile icon={Award} label="Embaixadoras ativas" value={totals.active} caption="com código valendo" />
              <StatTile icon={Users} label="Cadastros indicados" value={totals.signups} caption="contas criadas com código" />
              <StatTile
                icon={BadgeCheck}
                label="Assinaturas ativas"
                value={totals.subscriptions}
                caption={totals.signups ? `${Math.round((totals.subscriptions / totals.signups) * 100)}% dos indicados assinam` : "dos indicados"}
              />
            </div>

            <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  className="w-full rounded-xl border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm shadow-card focus:border-accent/40 focus:outline-none focus:ring-2 focus:ring-accent/15"
                  placeholder="Buscar por nome, e-mail ou código..."
                  onChange={(e) => setSearchTerm(e.target.value)}
                  value={searchTerm}
                />
              </div>
              <button
                onClick={openCreateModal}
                className={headerPrimaryButtonClass}
              >
                <Plus className="h-4 w-4" /> Nova embaixadora
              </button>
            </div>

            <div className="overflow-hidden rounded-2xl border border-gray-200/70 bg-white">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100">
                  <thead className="bg-subtle">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">#</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Embaixadora</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Código</th>
                      <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">Cadastros</th>
                      <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">Assinaturas ativas</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 hidden md:table-cell">Situação</th>
                      <th className="px-5 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {isLoading ? (
                      <tr><td colSpan="7" className="py-8 px-5 text-center text-sm text-gray-400">Carregando...</td></tr>
                    ) : filteredAmbassadors.length > 0 ? filteredAmbassadors.map((ambassador, index) => (
                      <tr key={ambassador._id} className={ambassador.isAmbassador ? "" : "opacity-60"}>
                        <td className="whitespace-nowrap px-5 py-3.5 text-sm text-gray-400">{index + 1}</td>
                        <td className="px-5 py-3.5">
                          <div className="text-sm font-medium text-gray-900">{ambassador.username}</div>
                          <div className="text-xs text-gray-500 truncate max-w-[220px]">{ambassador.email}</div>
                        </td>
                        <td className="whitespace-nowrap px-5 py-3.5">
                          <button
                            type="button"
                            onClick={() => copyCode(ambassador.code)}
                            className="inline-flex items-center gap-1.5 rounded-md bg-gray-100 px-2 py-1 font-mono text-xs font-semibold text-gray-800 hover:bg-gray-200"
                            title="Copiar código"
                          >
                            {ambassador.code} <Copy className="h-3 w-3 text-gray-500" />
                          </button>
                        </td>
                        <td className="whitespace-nowrap px-5 py-3.5 text-right text-sm font-semibold text-gray-900">{ambassador.signups}</td>
                        <td className="whitespace-nowrap px-5 py-3.5 text-right text-sm font-semibold text-gray-900">{ambassador.activeSubscriptions}</td>
                        <td className="whitespace-nowrap px-5 py-3.5 hidden md:table-cell">
                          <div className="flex gap-1.5">
                            <Badge className={ambassador.isAmbassador ? "bg-green-100 text-green-700 normal-case" : "bg-gray-100 text-gray-600 normal-case"}>
                              {ambassador.isAmbassador ? "Ativa" : "Desativada"}
                            </Badge>
                            {ambassador.dispatchesDisabled && <Badge className="bg-amber-100 text-amber-700 normal-case">Sem disparos</Badge>}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-5 py-3.5 text-right">
                          <div className="inline-flex gap-1">
                            <button
                              type="button"
                              onClick={() => setEditingCode({ _id: ambassador._id, username: ambassador.username, code: ambassador.code })}
                              disabled={ambassador.signups > 0}
                              className="rounded-md p-2 text-gray-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                              title={ambassador.signups > 0 ? "Já tem indicações — o código não pode mais ser trocado" : "Trocar código"}
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setToggleTarget(ambassador)}
                              className="rounded-md p-2 text-gray-500 hover:bg-gray-100"
                              title={ambassador.isAmbassador ? "Desativar" : "Reativar"}
                            >
                              <Power className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan="7" className="py-8 px-5 text-center text-sm text-gray-400">Nenhuma embaixadora cadastrada</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
      </AppShell>

      {/* Nova embaixadora */}
      <Modal title="Nova embaixadora" isOpen={showCreateModal} onClose={() => setShowCreateModal(false)}>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-1 rounded-lg bg-gray-100 p-1 text-sm">
            {[{ key: "existing", label: "Usuária existente" }, { key: "new", label: "Criar conta nova" }].map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => setField("mode", option.key)}
                className={`rounded-md px-3 py-1.5 font-medium ${form.mode === option.key ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"}`}
              >
                {option.label}
              </button>
            ))}
          </div>

          {form.mode === "existing" ? (
            <div>
              <label className={labelClass} htmlFor="amb-user-search">Buscar usuária</label>
              <input
                className={inputClass}
                id="amb-user-search"
                type="text"
                placeholder="Nome ou e-mail"
                value={userSearch}
                onChange={(e) => { setUserSearch(e.target.value); setField("selectedUser", null); }}
              />
              {form.selectedUser ? (
                <p className="mt-2 rounded-md bg-accent-light px-3 py-2 text-sm text-accent">
                  Selecionada: <strong>{form.selectedUser.username}</strong> ({form.selectedUser.email})
                </p>
              ) : candidateUsers.length > 0 && (
                <ul className="mt-2 divide-y divide-gray-100 rounded-md border border-gray-200">
                  {candidateUsers.map((user) => (
                    <li key={user._id}>
                      <button
                        type="button"
                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                        onClick={() => { setField("selectedUser", user); setUserSearch(user.username); }}
                      >
                        <span className="font-medium text-gray-900">{user.username}</span>
                        <span className="block text-xs text-gray-500">{user.email}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <>
              <div>
                <label className={labelClass} htmlFor="amb-username">Nome *</label>
                <input className={inputClass} id="amb-username" type="text" value={form.username} onChange={(e) => setField("username", e.target.value)} />
              </div>
              <div>
                <label className={labelClass} htmlFor="amb-email">E-mail *</label>
                <input className={inputClass} id="amb-email" type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} />
              </div>
              <div>
                <label className={labelClass} htmlFor="amb-phone">Telefone *</label>
                <input className={inputClass} id="amb-phone" type="tel" placeholder="(11) 99999-9999" value={form.phone} onChange={(e) => setField("phone", e.target.value)} />
              </div>
              <div>
                <label className={labelClass} htmlFor="amb-password">Senha *</label>
                <input className={inputClass} id="amb-password" type="password" value={form.password} onChange={(e) => setField("password", e.target.value)} />
              </div>
            </>
          )}

          <div>
            <label className={labelClass} htmlFor="amb-code">Código de indicação *</label>
            <input
              className={`${inputClass} uppercase`}
              id="amb-code"
              type="text"
              placeholder="GABI10"
              maxLength={20}
              value={form.code}
              onChange={(e) => setField("code", normalizeReferralCode(e.target.value))}
            />
            <p className="text-xs text-gray-400 mt-1">3 a 20 letras ou números, sem espaços. Sempre em maiúsculas e único por embaixadora.</p>
          </div>

          <CheckboxField
            id="amb-dispatches-disabled"
            checked={form.dispatchesDisabled}
            onChange={(value) => setField("dispatchesDisabled", value)}
            label="Sem disparos (só push)"
            help="Sem WhatsApp nem e-mail de disparo para esta conta. O push do app continua."
          />

          <div className="flex gap-2 pt-2">
            <button className={primaryButtonClass} onClick={handleCreate} disabled={isSaving}>
              {isSaving ? "Salvando..." : "Cadastrar embaixadora"}
            </button>
            <button className={secondaryButtonClass} onClick={() => setShowCreateModal(false)} disabled={isSaving}>
              Cancelar
            </button>
          </div>
        </div>
      </Modal>

      {/* Trocar código */}
      <Modal title={`Trocar código de ${editingCode?.username || ""}`} isOpen={!!editingCode} onClose={() => setEditingCode(null)}>
        {editingCode && (
          <div className="space-y-4">
            <div>
              <label className={labelClass} htmlFor="amb-edit-code">Novo código</label>
              <input
                className={`${inputClass} uppercase`}
                id="amb-edit-code"
                type="text"
                maxLength={20}
                value={editingCode.code}
                onChange={(e) => setEditingCode((prev) => ({ ...prev, code: normalizeReferralCode(e.target.value) }))}
              />
              <p className="text-xs text-gray-400 mt-1">Só dá pra trocar enquanto a embaixadora não tem indicações. Se o link/afiliado da Kiwify usa o código antigo, atualize lá também.</p>
            </div>
            <div className="flex gap-2 pt-2">
              <button className={primaryButtonClass} onClick={handleSaveCode} disabled={isSaving}>{isSaving ? "Salvando..." : "Salvar"}</button>
              <button className={secondaryButtonClass} onClick={() => setEditingCode(null)} disabled={isSaving}>Cancelar</button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmModal
        isOpen={!!toggleTarget}
        title={toggleTarget?.isAmbassador ? "Desativar embaixadora" : "Reativar embaixadora"}
        message={toggleTarget?.isAmbassador
          ? `O código ${toggleTarget?.code} deixa de valer em cadastros novos. As indicações antigas continuam atribuídas a ${toggleTarget?.username}.`
          : `O código ${toggleTarget?.code} volta a valer em cadastros novos.`}
        confirmLabel={toggleTarget?.isAmbassador ? "Desativar" : "Reativar"}
        danger={Boolean(toggleTarget?.isAmbassador)}
        onCancel={() => setToggleTarget(null)}
        onConfirm={handleToggleActive}
      />
    </>
  );
};
